import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, retry, timer, throwError } from 'rxjs';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { APP_CONFIG } from '../config/app.config';
import { EnvironmentUtil } from '../utils/environment.util';

/**
 * Intercepteur de gestion des erreurs HTTP
 * 
 * Fonctionnalités:
 * - Retry automatique pour les erreurs réseau et 5xx
 * - Logging détaillé des erreurs
 * - Messages utilisateur via PrimeNG Toast
 * - Gestion spécifique par type d'erreur
 * 
 * Pattern: Exponential Backoff Retry
 * Les retries sont espacés progressivement (delay * retryCount)
 */

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
    // Note: on ne peut pas injecter des services singleton ici car c'est une fonction
    // mais on peut le faire dans le catchError

    // Retry réservé aux méthodes idempotentes : rejouer un POST/PATCH crée des doublons.
    const isIdempotent = req.method === 'GET' || req.method === 'HEAD' || req.method === 'OPTIONS';

    return next(req).pipe(
        retry({
            count: isIdempotent ? APP_CONFIG.retryAttempts : 0,
            delay: (error: HttpErrorResponse, retryCount: number) => {
                // Only retry on network errors or 5xx errors
                const shouldRetry = error.status === 0 || (error.status >= 500 && error.status < 600);

                if (shouldRetry) {
                    const delayMs = APP_CONFIG.retryDelay * retryCount;
                    EnvironmentUtil.log(`🔄 Retry attempt ${retryCount}/${APP_CONFIG.retryAttempts} after ${delayMs}ms`);
                    return timer(delayMs);
                }

                // Return error immediately (no retry)
                throw error;
            },
        }),
        catchError((error: HttpErrorResponse) => {
            // Log détaillé pour monitoring/debugging
            console.group(`❌ HTTP Error: ${error.status || 'Network'}`);
            console.error('URL:', req.url);
            console.error('Method:', req.method);
            console.error('Status:', error.status);
            console.error('Message:', error.message);
            console.error('Error Details:', error.error);
            console.groupEnd();

            // Gérer les cas spécifiques
            handleSpecificErrors(error, req);

            return throwError(() => error);
        }),
    );
};

/**
 * Gérer les erreurs spécifiques avec des messages appropriés
 */
function handleSpecificErrors(error: HttpErrorResponse, req: any): void {
    // Erreur réseau (pas de connexion)
    if (error.status === 0) {
        console.error('🌐 Network error - check connectivity');
        // On pourrait afficher un toast ici si nécessaire
    }

    // Erreur Bad Request
    else if (error.status === 400) {
        console.warn('⚠️ Bad Request:', error.error?.message || error.message);
    }

    // Erreur serveur
    else if (error.status >= 500 && error.status < 600) {
        console.error('🔥 Server error:', error.message);
        // On pourrait logger vers un service de monitoring (Sentry, etc.)
    }

    // Timeout
    else if (error.status === 504 || error.message.includes('timeout')) {
        console.error('⏱️ Request timeout');
    }

    // Too Many Requests (Rate limiting)
    else if (error.status === 429) {
        console.warn('⚠️ Too many requests - rate limited');
        const retryAfter = error.headers?.get('Retry-After');
        if (retryAfter) {
            // retry info available
        }
    }
}

/**
 * Formater l'erreur pour l'affichage utilisateur
 */
export function formatErrorMessage(error: HttpErrorResponse): string {
    if (error.status === 0) {
        return 'Erreur de connexion. Vérifiez votre connexion internet.';
    }

    if (error.status === 404) {
        return 'Ressource non trouvée.';
    }

    if (error.status >= 500) {
        return 'Erreur serveur. Veuillez réessayer plus tard.';
    }

    // Utiliser le message de l'API si disponible
    if (error.error?.message) {
        if (typeof error.error.message === 'string') {
            return error.error.message;
        }
        if (Array.isArray(error.error.message)) {
            return error.error.message.join(', ');
        }
    }

    return error.message || 'Une erreur est survenue.';
}

